import { t } from "adapters/i18n";
import type { Dispatch } from "react-redux";
import { notifyError } from "redux/actions";
import {
  fetchBenchmarkAccessForLoggedUserAction,
  setBenchmarkEditUserAssociacoesAction,
  setBenchmarkEditUserCarregandoAction,
  setBenchmarkEditUserStatusAction,
  type BenchmarkDispatchAction,
} from "redux/actions/benchmarkActions";
import type { RootState } from "redux/reducers";
import type {
  BenchmarkGrantBody,
  BenchmarkRevokeBody,
  BenchmarkUserStatusResponse,
} from "services/BenchmarkService";
import BenchmarkService from "services/BenchmarkService";
import FazendaService from "services/FazendaService";
import { getErrorMessage } from "shared/errorUtils";
import { Cliente } from "shared/types";

export const fetchBenchmarkAccessForLoggedUser =
  () =>
  async (
    dispatch: Dispatch<BenchmarkDispatchAction>,
    getState: () => RootState,
  ): Promise<void> => {
    try {
      const rootState = getState();
      const loggedUser = rootState.usuario.Usuario;

      if (!loggedUser?.Email) {
        dispatch(
          fetchBenchmarkAccessForLoggedUserAction({
            hasAccess: false,
            hasContextualAccess: false,
          }),
        );
        return;
      }

      const selectedClientId = rootState.cliente?.ClienteSelecionado?.IdCliente;
      const nameSelectedClient = selectedClientId
        ? rootState.cliente?.Clientes.find(
            (c: Cliente) => c.IdCliente === selectedClientId,
          )?.Nome
        : undefined;
      const selectedFarmId = rootState.fazenda?.FazendaSelecionada;
      const nameSelectedFarm = selectedFarmId
        ? rootState.fazenda?.Fazendas.find(
            (f) => f.IdFazenda === selectedFarmId,
          )?.Nome
        : undefined;

      const statusResponse = await BenchmarkService.getAcessoBenchmark(
        loggedUser.Email,
        nameSelectedClient,
        nameSelectedFarm,
      );

      dispatch(
        fetchBenchmarkAccessForLoggedUserAction({
          hasAccess: statusResponse.hasUserAccess,
          hasContextualAccess: statusResponse.hasContextualAccess,
        }),
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("benchmark:fetch-benchmark-access-error"),
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const fetchBenchmarkAssociacoes =
  (clientId: number) =>
  async (
    dispatch: Dispatch<BenchmarkDispatchAction>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(setBenchmarkEditUserCarregandoAction(true));

    try {
      const nomeCliente = getState().cliente.ClienteSelecionado?.Nome ?? null;

      if (!nomeCliente) {
        dispatch(setBenchmarkEditUserAssociacoesAction([]));
        return;
      }

      const farmsResponse =
        await FazendaService.listarFazendasPorCliente(clientId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const farmNames: string[] =
        farmsResponse.data?.map((f: any) => f.Nome) ?? [];

      if (farmNames.length === 0) {
        dispatch(setBenchmarkEditUserAssociacoesAction([]));
        return;
      }

      const farmAccessResponse =
        await BenchmarkService.getClienteFazendasBenchmarkAccess(
          nomeCliente,
          farmNames,
        );

      dispatch(
        setBenchmarkEditUserAssociacoesAction([
          { nomeCliente, fazendas: farmAccessResponse.farms },
        ]),
      );
    } catch (error) {
      dispatch(setBenchmarkEditUserCarregandoAction(false));
      const message = getErrorMessage(
        error,
        t("benchmark:fetch-benchmark-access-error"),
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const fetchBenchmarkStatusForEditUser =
  (email: string) =>
  async (
    dispatch: Dispatch<BenchmarkDispatchAction>,
  ): Promise<BenchmarkUserStatusResponse | null> => {
    try {
      const statusResponse = await BenchmarkService.getAcessoBenchmark(email);
      dispatch(
        setBenchmarkEditUserStatusAction({
          localUserStatus: statusResponse.localUserStatus,
          hasUserAccess: statusResponse.hasUserAccess,
        }),
      );
      return statusResponse;
    } catch {
      return null;
    }
  };

export const grantRevokeBenchmarkAccess =
  (email: string, body: BenchmarkGrantBody | BenchmarkRevokeBody) =>
  async (_dispatch: Dispatch<BenchmarkDispatchAction>): Promise<void> => {
    try {
      await BenchmarkService.putAcessoBenchmark(email, body);
    } catch (error: unknown) {
      const apiMessage = extractApiErrorMessage(error);
      throw new Error(apiMessage ?? "");
    }
  };

function extractApiErrorMessage(error: unknown): string | null {
  if (error == null || typeof error !== "object" || !("response" in error)) {
    return null;
  }
  const responseData = (error as { response?: { data?: unknown } }).response
    ?.data;
  if (typeof responseData === "string") return responseData;
  if (responseData != null && typeof responseData === "object") {
    const obj = responseData as { message?: unknown; error?: unknown };
    const msg = obj.message ?? obj.error;
    if (msg != null) return String(msg) || null;
  }
  return null;
}
